// Segment page — solo publishers. Replaces the MarketingShell-based stub
// at /pour/editeurs-solo with a richer landing: pain → solution, pricing
// teaser, daily-routine timeline, pullquote, final CTA.

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sunrise, Sun, Moon, ArrowRight, Sparkles } from "lucide-react";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Pullquote } from "@/components/marketing/Pullquote";
import { Rule } from "@/components/marketing/Rule";
import { MarketingSegmentLayout } from "@/components/marketing/MarketingSegmentLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { withUtm } from "@/lib/marketing-utm";
import { PLAN_DISPLAY_NAMES, PLAN_MONTHLY_CENTS } from "@/lib/marketing-plan-matrix";
import { useLocale } from "@/lib/locale";
import { formatMoney, planMonthlyCents } from "@/lib/currency";

const UTM_CAMPAIGN = "editeurs-solo";

const TIMELINE_KEYS = [
  { key: "morning", Icon: Sunrise },
  { key: "noon", Icon: Sun },
  { key: "evening", Icon: Moon },
] as const;

export default function SoloPublishersPage() {
  const { t, i18n } = useTranslation();
  const locale = useLocale();

  const pains = [0, 1, 2].map((i) => ({
    title: t(`marketing.pages.soloPublishers.pains.${i}.title`),
    body: t(`marketing.pages.soloPublishers.pains.${i}.body`),
  }));
  const solutions = [0, 1, 2].map((i) => ({
    title: t(`marketing.pages.soloPublishers.solutions.${i}.title`),
    body: t(`marketing.pages.soloPublishers.solutions.${i}.body`),
  }));
  const starterPrice = formatMoney(planMonthlyCents("starter")!, locale);

  return (
    <MarketingSegmentLayout
      seoTitle={t("marketing.pages.soloPublishers.seo.title")}
      seoDescription={t("marketing.pages.soloPublishers.seo.description")}
      path="/pour/editeurs-solo"
      breadcrumbName={t("marketing.header.personas.soloEditors.title")}
      eyebrow={t("marketing.pages.soloPublishers.hero.eyebrow")}
      heroTitle={t("marketing.pages.soloPublishers.hero.title")}
      heroLead={t("marketing.pages.soloPublishers.hero.lead")}
      pains={pains}
      painsHeading={t("marketing.pages.soloPublishers.painsHeading")}
      solutions={solutions}
      solutionsHeading={t("marketing.pages.soloPublishers.solutionsHeading")}
      utmCampaign={UTM_CAMPAIGN}
      ctaHeadline={t("marketing.pages.soloPublishers.cta.headline")}
      ctaSubhead={t("marketing.pages.soloPublishers.cta.subhead")}
      primaryCtaLabel={t("marketing.pages.soloPublishers.cta.primary")}
      primaryCtaTo="/dpadmin?intent=signup&plan=starter"
      secondaryCtaLabel={t("marketing.cta.bookDemo")}
      secondaryCtaTo="/contact?subject=editeurs-solo"
    >
      <Rule />

      {/* Pricing teaser — Starter highlighted */}
      <Section padding="md">
        <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-[1.2fr_1fr]">
          <div>
            <Eyebrow>{t("marketing.pages.soloPublishers.pricingTeaser.eyebrow")}</Eyebrow>
            <DisplayHeading level={2} className="mt-4">
              {t("marketing.pages.soloPublishers.pricingTeaser.title")}
            </DisplayHeading>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              {t("marketing.pages.soloPublishers.pricingTeaser.lead")}
            </p>
            <Link
              to={withUtm("/tarifs", { source: "segment", campaign: UTM_CAMPAIGN, content: "pricing-teaser" })}
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-accent-press hover:underline"
            >
              {t("marketing.pages.soloPublishers.pricingTeaser.cta")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>

          <article className="border border-accent-press bg-background p-6 ring-2 ring-accent-press/30">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-medium tracking-tight">
                  {locale !== "fr" ? PLAN_DISPLAY_NAMES.starter.en : PLAN_DISPLAY_NAMES.starter.fr}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("marketing.pages.soloPublishers.pricingTeaser.audience")}
                </p>
              </div>
              <Badge>{t("marketing.pages.soloPublishers.pricingTeaser.recommended")}</Badge>
            </div>
            <div className="mt-6 text-4xl font-medium tabular-nums tracking-tight">{starterPrice}</div>
            <p className="mt-1 text-xs text-muted-foreground">{t("marketing.pages.tarifs.plans.perMonth")}</p>
            <ul className="mt-6 space-y-2">
              {(t("marketing.pages.tarifs.plans.features.starter", { returnObjects: true }) as string[])
                .slice(0, 4)
                .map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden />
                    <span>{f}</span>
                  </li>
                ))}
            </ul>
            <Button asChild className="mt-6 w-full">
              <Link to={withUtm("/dpadmin?intent=signup&plan=starter", { source: "segment", campaign: UTM_CAMPAIGN, content: "starter-card" })}>
                <Sparkles className="mr-2 h-4 w-4" aria-hidden />
                {t("marketing.pages.tarifs.plans.ctaSelect", { plan: locale !== "fr" ? PLAN_DISPLAY_NAMES.starter.en : PLAN_DISPLAY_NAMES.starter.fr })}
              </Link>
            </Button>
          </article>
        </div>
      </Section>

      <Rule />

      {/* Daily routine timeline */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.soloPublishers.routine.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.soloPublishers.routine.title")}
          </DisplayHeading>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {t("marketing.pages.soloPublishers.routine.lead")}
          </p>
        </div>

        <ol className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {TIMELINE_KEYS.map(({ key, Icon }, i) => (
            <li key={key} className="border-l-2 border-foreground bg-background pl-5 pr-3 py-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                <Icon className="h-4 w-4" aria-hidden />
                {t(`marketing.pages.soloPublishers.routine.steps.${key}.time`)}
              </div>
              <h3 className="mt-3 text-base font-medium tracking-tight">
                {t(`marketing.pages.soloPublishers.routine.steps.${key}.title`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {t(`marketing.pages.soloPublishers.routine.steps.${key}.body`)}
              </p>
              <span className="sr-only">{`${i + 1}/3`}</span>
            </li>
          ))}
        </ol>
      </Section>

      {/* Testimonial pullquote — placeholder until real customer permission. */}
      <Section padding="md" tone="paper">
        <Pullquote
          quote={t("marketing.pages.soloPublishers.testimonial.quote")}
          attribution={t("marketing.pages.soloPublishers.testimonial.attribution")}
        />
      </Section>
    </MarketingSegmentLayout>
  );
}
